import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import styles from './TimelineView.module.scss'
import TimelineBox from '@/component/Primitive/Events/components/EventAgendaNavigator/components/Timeline/TimelineBox'
// import CurrentTimeIndicator from '@/component/Primitive/Events/components/EventAgendaNavigator/components/Timeline/CurrentTimeIndicator'
import Type from '@/component/Primitive/Type'

// Utility Functions
const formatDate = (dateString) => moment(dateString).format('D.M.YYYY')

const getMinutesFromMidnight = (date) =>
  date.getUTCHours() * 60 + date.getUTCMinutes()

// Separate Utility: Dynamic Grid Settings Calculation
const calculateDynamicGridSettings = (dayAgenda) => {
  if (dayAgenda.length === 0) return null

  // Sort events by start time
  const sortedEvents = dayAgenda.sort(
    (a, b) => new Date(a.startDate) - new Date(b.startDate)
  )

  const firstEvent = sortedEvents[0]
  const lastEvent = sortedEvents[sortedEvents.length - 1]
  const firstEventStart = new Date(firstEvent.startDate)
  const lastEventEnd = new Date(lastEvent.endDate)

  // Calculate start and end times in minutes from midnight with buffers
  const bufferMinutes = 30 // 30-minute buffer
  const startMinutes =
    Math.round(getMinutesFromMidnight(firstEventStart) / 30) * 30 -
    bufferMinutes
  const endMinutes =
    Math.ceil(getMinutesFromMidnight(lastEventEnd) / 30) * 30 + bufferMinutes

  // Calculate grid column start and end
  const startColumn = Math.floor(startMinutes / 5) + 1
  const endColumn = Math.floor(endMinutes / 5) + 1

  return {
    startColumn,
    endColumn,
    gridColumns: endColumn - startColumn + 1,
    numTimeLabels: Math.ceil((endColumn - startColumn + 1) / 6) + 2, // Add extra labels for buffer
    startMinutes,
    endMinutes
  }
}

// Separate Utility: Mapping to Grid Columns
const mapToGridColumns = (item, dynamicSettings) => {
  if (!dynamicSettings) return { c1: 1, c2: 1 }

  const d1 = new Date(item.startDate)
  const d2 = new Date(item.endDate)
  const c1 =
    Math.floor(getMinutesFromMidnight(d1) / 5) +
    1 -
    dynamicSettings.startColumn +
    1

  const c2 =
    Math.floor(getMinutesFromMidnight(d2) / 5) +
    1 -
    dynamicSettings.startColumn +
    1 -
    c1

  return { c1, c2 }
}

// Component: TimelineLabels
const TimelineLabels = ({ gridColumns, numTimeLabels, startMinutes = 0 }) => {
  const getTimeLabel = (minutesFromMidnight) => {
    const hours = Math.floor(minutesFromMidnight / 60) + 1
    const mins = Math.floor(minutesFromMidnight % 60)
    return `${hours}.${mins === 0 ? '00' : mins}`
  }

  const timeLabels = Array.from({ length: numTimeLabels }, (_, i) => {
    const minutesFromMidnight = startMinutes + i * 30
    const span = Math.ceil(gridColumns / (numTimeLabels - 1))
    return {
      index: i,
      c1: Math.ceil(i * span + 1),
      c2: span,
      time: getTimeLabel(minutesFromMidnight)
    }
  })

  return (
    <>
      {timeLabels.map((label, i) => (
        <div
          key={i}
          className={styles.TimeLabel}
          style={{
            gridColumn: `${label.c1} / span ${label.c2}`,
            gridRow: '1 / span 1'
          }}
        >
          <span className={styles.TimeLabelValue}>
            <Type as="span" size={['body3', 'body3']}>
              {label.time}
            </Type>
          </span>
        </div>
      ))}
    </>
  )
}

TimelineLabels.propTypes = {
  gridColumns: PropTypes.number.isRequired,
  numTimeLabels: PropTypes.number.isRequired,
  startMinutes: PropTypes.number
}

// Component: StageLabels
const StageLabels = ({
  timelineHeight,
  timelinePaddingTop,
  timelinePaddingBottom,
  timelineLabelsHeight,
  timelineLabelsWidth,
  locations
}) => {
  return (
    <div
      className={styles.StageLabels}
      style={{ width: timelineLabelsWidth + 'px' }}
    >
      <div
        className={styles.StageLabelsInner}
        style={{
          height:
            timelineHeight - timelinePaddingBottom - timelinePaddingTop + 'px',
          position: 'relative',
          top: timelineLabelsHeight + 'px'
        }}
      >
        {locations.map((location, i) => (
          <div
            key={i}
            className={styles.StageLabel}
            style={{ height: 100 / locations.length + '%' }}
          >
            <Type className={styles.StageLabelInner} size={['body3', 'body3']}>
              {location}
            </Type>
          </div>
        ))}
      </div>
    </div>
  )
}

StageLabels.propTypes = {
  timelineHeight: PropTypes.number.isRequired,
  timelinePaddingTop: PropTypes.number.isRequired,
  timelinePaddingBottom: PropTypes.number.isRequired,
  timelineLabelsHeight: PropTypes.number.isRequired,
  timelineLabelsWidth: PropTypes.number.isRequired,
  locations: PropTypes.arrayOf(PropTypes.string).isRequired
}

// Component: ScrollArea
const ScrollArea = ({
  children,
  gridColumns,
  gridRows,
  timelineWidth,
  timelineHeight,
  timelinePaddingTop,
  timelinePaddingBottom,
  timelineLabelsHeight,
  locations
}) => {
  return (
    <div
      className={styles.ScrollArea}
      style={{
        width: timelineWidth + 'px',
        height: timelineHeight + 'px',
        paddingTop: timelinePaddingTop + 'px',
        paddingBottom: timelinePaddingBottom + 'px'
      }}
    >
      <StageLabels
        timelineHeight={timelineHeight}
        timelinePaddingTop={timelinePaddingTop}
        timelinePaddingBottom={timelinePaddingBottom}
        timelineLabelsHeight={timelineLabelsHeight}
        timelineLabelsWidth={100}
        locations={locations}
      />

      <div
        className={styles.ScrollAreaInner}
        style={{
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          gridTemplateRows: `${timelineLabelsHeight}px repeat(${gridRows}, 3fr)`
        }}
      >
        {children}
      </div>
    </div>
  )
}

ScrollArea.propTypes = {
  children: PropTypes.node,
  gridColumns: PropTypes.number.isRequired,
  gridRows: PropTypes.number.isRequired,
  timelineWidth: PropTypes.number.isRequired,
  timelineHeight: PropTypes.number.isRequired,
  timelinePaddingTop: PropTypes.number.isRequired,
  timelinePaddingBottom: PropTypes.number.isRequired,
  timelineLabelsHeight: PropTypes.number.isRequired,
  locations: PropTypes.arrayOf(PropTypes.string).isRequired
}

// Main Timeline Component
const Timeline = ({
  event,
  agendaItemDayMap,
  selectedDay,
  currentDateItem,
  timezoneAbbreviation
}) => {
  const { agenda, timezone, _registerButtonClassName, _registerAction } = event
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [groupedAgenda, setGroupedAgenda] = useState({})
  const [currentTime /* setCurrentTime */] = useState({ c1: 0, c2: 0 })
  const timelineRef = useRef(null)
  const [scrollPosition, setScrollPosition] = useState(null)

  // Mouse interaction handlers
  const handleMouseDown = useCallback((event) => {
    if (!timelineRef.current) return

    setIsDragging(true)
    // eslint-disable-next-line react/prop-types
    setStartX(event.pageX - timelineRef.current.offsetLeft)
    setScrollLeft(timelineRef.current.scrollLeft)
    // eslint-disable-next-line react/prop-types
    event.preventDefault()
  }, [])

  const handleMouseMove = useCallback(
    (event) => {
      if (!isDragging || !timelineRef.current) return

      // eslint-disable-next-line react/prop-types
      const x = event.pageX - timelineRef.current.offsetLeft
      const walk = (x - startX) * 2

      timelineRef.current.scrollLeft = scrollLeft - walk
    },
    [isDragging, startX, scrollLeft]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Event listeners for mouse interactions
  useEffect(() => {
    const timelineElement = timelineRef.current
    const abortController = new AbortController()

    if (timelineElement) {
      timelineElement.addEventListener(
        'mouseleave',
        handleMouseUp,
        abortController
      )
      document.addEventListener('mouseup', handleMouseUp, abortController)
      document.addEventListener('mousemove', handleMouseMove, abortController)

      return () => {
        abortController.abort()
      }
    }
  }, [handleMouseMove, handleMouseUp])

  // Filtered locations for the selected day
  const filteredLocations = useMemo(() => {
    const selectedDayIndex = selectedDay.split('Day ')[1]

    const formattedSelectedDate = Object.keys(agendaItemDayMap).find(
      (formattedDate) =>
        Number(agendaItemDayMap[formattedDate].dayIndex) ===
        Number(selectedDayIndex)
    )

    return agendaItemDayMap[formattedSelectedDate].stages
  }, [agendaItemDayMap, selectedDay])

  // Grid settings calculation
  const gridSettings = useMemo(() => {
    const dayAgenda = agenda.filter(
      (item) =>
        formatDate(item.startDate) === formatDate(currentDateItem.startDate)
    )
    const dynamicSettings = calculateDynamicGridSettings(dayAgenda)

    return dynamicSettings
      ? {
          gridColumns: dynamicSettings.gridColumns,
          gridRows: filteredLocations.length,
          numTimeLabels: dynamicSettings.numTimeLabels,
          timelineWidth: dynamicSettings.gridColumns * 50,
          timelineHeight: 150 + 100 * (filteredLocations.length - 1),
          timelinePaddingTop: 30,
          timelinePaddingBottom: 20,
          timelineLabelsHeight: 10,
          timelineLabelsWidth: 100,
          startColumn: dynamicSettings.startColumn,
          startMinutes: dynamicSettings.startMinutes,
          endMinutes: dynamicSettings.endMinutes
        }
      : null
  }, [agenda, currentDateItem.startDate, filteredLocations.length])

  // Grouped agenda creation
  useEffect(() => {
    const dayAgenda = agenda.filter(
      (item) =>
        formatDate(item.startDate) === formatDate(currentDateItem.startDate)
    )
    const dynamicSettings = calculateDynamicGridSettings(dayAgenda)

    setGroupedAgenda(
      agenda.reduce((acc, item) => {
        const dayName = `Day ${
          agendaItemDayMap[formatDate(item.startDate)].dayIndex
        }`

        if (!acc[dayName]) {
          acc[dayName] = { stages: {} }
        }

        if (!acc[dayName].stages[item.location.name]) {
          acc[dayName].stages[item.location.name] = {
            order: agendaItemDayMap[formatDate(item.startDate)].stages.indexOf(
              item.location.name
            ),
            items: []
          }
        }

        const { c1, c2 } = mapToGridColumns(item, dynamicSettings)

        acc[dayName].stages[item.location.name].items.push({
          ...item,
          c1,
          c2
        })

        acc[dayName].stages[item.location.name].items.sort(
          (a, b) => new Date(a.startDate) - new Date(b.startDate)
        )

        return acc
      }, {})
    )
  }, [agenda, filteredLocations, agendaItemDayMap, currentDateItem])

  // Scroll handling
  const handleScroll = useCallback((e) => {
    if (e.target) {
      setScrollPosition(e.target.scrollLeft)
    }
  }, [])

  // Initial scroll positioning
  const getInitialScrollLeft = useCallback(
    (timelineRefWidth = 0) => {
      const sortedAgenda = event.agenda.sort((a, b) => {
        return new Date(a.startDate) - new Date(b.startDate)
      })
      const firstDate = new Date(sortedAgenda[0].startDate)
      const lastDate = new Date(sortedAgenda[sortedAgenda.length - 1].endDate)
      const { startMinutes, endMinutes } = gridSettings
      const totalMinutes = endMinutes - startMinutes
      const firstMinutes = getMinutesFromMidnight(firstDate)
      const lastMinutes = getMinutesFromMidnight(lastDate)
      const nowMinutes = getMinutesFromMidnight(new Date())
      let date, offset

      if (nowMinutes >= firstMinutes && nowMinutes <= lastMinutes) {
        date = new Date()
        offset =
          (timelineRefWidth + 64) /* From spacing between timeline & side */ /
            2 -
          gridSettings.timelineLabelsWidth
      } else {
        date = firstDate
        offset = gridSettings.timelineLabelsWidth
      }

      const initialMinutes = getMinutesFromMidnight(date)
      const initialLeft =
        (initialMinutes / totalMinutes) * gridSettings.timelineWidth

      return initialLeft - offset
    },
    [event.agenda, gridSettings]
  )

  // Scroll positioning effect
  useEffect(() => {
    const timelineElement = timelineRef.current

    if (timelineElement) {
      if (scrollPosition === null) {
        timelineElement.scrollLeft = getInitialScrollLeft(
          timelineElement.offsetWidth
        )
      } else {
        timelineElement.scrollLeft = scrollPosition
      }
    }
  }, [
    timelineRef,
    scrollPosition,
    getInitialScrollLeft,
    handleScroll,
    currentDateItem
  ])

  // Render
  if (Object.keys(groupedAgenda).length === 0 || !gridSettings) return null

  getInitialScrollLeft()

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={styles.Timeline}
      ref={timelineRef}
      style={{
        overflowX: 'scroll',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
      onScroll={handleScroll}
    >
      <ScrollArea {...gridSettings} locations={filteredLocations}>
        {/* - DISABLED AS DOESN'T ACCOUNT FOR TIMEZONE RN, SAME AS EVENTCONTENTGRID & TIMELINE 
        <CurrentTimeIndicator
          timelineWidth={gridSettings.timelineWidth}
          gridColumns={gridSettings.gridColumns}
          onUpdate={setCurrentTime}
          currentTime={currentTime}
        /> */}

        {Object.keys(groupedAgenda[selectedDay].stages).map((location, li) => {
          const sortedAgenda = groupedAgenda[selectedDay].stages[location]

          return (
            <React.Fragment key={location}>
              {sortedAgenda.items.map((item, ii) => (
                <TimelineBox
                  {...item}
                  r1={sortedAgenda.order + 2}
                  r2={1}
                  key={`${li}_${ii}`}
                  currentTime={currentTime}
                  timezone={timezone}
                  registerButtonClassName={_registerButtonClassName}
                  registerAction={_registerAction}
                  timeRange={`${item.localeSafeStartTime} - ${item.localeSafeEndTime} (${timezoneAbbreviation})`}
                />
              ))}
            </React.Fragment>
          )
        })}

        <TimelineLabels {...gridSettings} />
      </ScrollArea>
    </div>
  )
}

Timeline.propTypes = {
  event: PropTypes.shape({
    agenda: PropTypes.array.isRequired,
    timezone: PropTypes.string,
    _registerButtonClassName: PropTypes.string,
    _registerAction: PropTypes.func
  }).isRequired,
  sortedAgenda: PropTypes.array,
  agendaItemDayMap: PropTypes.object.isRequired,
  selectedDay: PropTypes.string.isRequired,
  currentDateItem: PropTypes.object.isRequired,
  timezoneAbbreviation: PropTypes.string
}

export default Timeline
